import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { Button } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }

    return [];
};

const CompanyManagement = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();

    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const companyId = useMemo(
        () => userData?.company_id || userData?.companyId || userData?.company,
        [userData]
    );

    const childConditions = useMemo(() => {
        return companyId ? { company_id: Number(companyId) } : {};
    }, [companyId]);

    const fetchAgents = useCallback(async () => {
        setLoading(true);
        try {
            const agentRes = await oneKlicks.read("", "insurance_agents", childConditions, "created_at DESC", "", "", "0,200");
            const agentRows = normaliseResponse(agentRes);
            setAgents(agentRows);
        } catch (error) {
            console.error("Error loading agents:", error);
            toast.error("Failed to load agents");
            setAgents([]);
        } finally {
            setLoading(false);
        }
    }, [childConditions]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const filteredAgents = useMemo(() => {
        if (!searchText.trim()) {
            return agents;
        }
        const searchLower = searchText.toLowerCase();
        return agents.filter((agent) => {
            return (
                agent.full_name?.toLowerCase().includes(searchLower) ||
                agent.name?.toLowerCase().includes(searchLower) ||
                agent.agent_email?.toLowerCase().includes(searchLower) ||
                agent.email?.toLowerCase().includes(searchLower) ||
                agent.agent_phone?.toLowerCase().includes(searchLower) ||
                agent.phone?.toLowerCase().includes(searchLower) ||
                agent.company_name?.toLowerCase().includes(searchLower) ||
                agent.designation?.toLowerCase().includes(searchLower) ||
                agent.license_number?.toLowerCase().includes(searchLower)
            );
        });
    }, [agents, searchText]);

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Agents Management Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Agents Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Agents</Badge>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        className="btn-outline-primary" 
                                        onClick={fetchAgents}
                                        disabled={loading}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <ReloadOutlined /> Refresh
                                    </Button>
                                    <Button 
                                        className="btn-primary" 
                                        onClick={() => navigate("/insurance/agents/new")}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <PlusOutlined /> Add New Agent
                                    </Button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search agents..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Agents Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredAgents.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Agent</th>
                                                <th>Company</th>
                                                <th>Contact</th>
                                                <th>License</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAgents.map((agent) => (
                                                <tr key={agent.id}>
                                                    <td data-label="ID">{agent.id}</td>
                                                    <td data-label="Agent">
                                                        <div>
                                                            <div style={{ fontWeight: '500', color: '#333' }}>
                                                                {agent.full_name || agent.name || `Agent #${agent.id}`}
                                                            </div>
                                                            {agent.designation && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                    {agent.designation}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Company">
                                                        <span className="badge badge-info" style={{ borderRadius: "5px", fontSize: '12px', padding: '4px 10px' }}>
                                                            {agent.company_name || agent.company || "—"}
                                                        </span>
                                                    </td>
                                                    <td data-label="Contact">
                                                        <div>
                                                            {agent.agent_email || agent.email ? (
                                                                <div style={{ fontSize: '13px', color: '#333', marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
                                                                    <i className="ti-email" style={{ marginRight: '6px', color: '#666', fontSize: '14px' }}></i>
                                                                    <span>{agent.agent_email || agent.email}</span>
                                                                </div>
                                                            ) : null}
                                                            {agent.agent_phone || agent.phone ? (
                                                                <div style={{ fontSize: '13px', color: '#333', display: 'flex', alignItems: 'center' }}>
                                                                    <i className="ti-mobile" style={{ marginRight: '6px', color: '#666', fontSize: '14px' }}></i>
                                                                    <span>{agent.agent_phone || agent.phone}</span>
                                                                </div>
                                                            ) : null}
                                                            {!agent.agent_email && !agent.email && !agent.agent_phone && !agent.phone && (
                                                                <span style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="License" style={{ fontSize: '13px', color: '#666' }}>
                                                        {agent.license_number || agent.registration_number || "—"}
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-info" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle edit
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Edit
                                                            </Button>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-danger" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // Handle delete
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No agents added yet</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyManagement;

